import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import { ChevronDown, ExternalLink } from "lucide-react";
import type { InputResultLabel } from "../../../queries/reports";
import { TruncatedHoverText } from "./truncated-hover-text";

interface DisplayInputResultLabelsProps {
  labels: InputResultLabel[];
  className?: string;
}

export function DisplayInputResultLabels({
  labels,
  className,
}: DisplayInputResultLabelsProps) {
  if (!labels.length) return null;

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {labels.map((label) =>
        label.children !== undefined ? (
          label.children.length > 0 ? (
            <DropdownMenu key={label.key}>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-5 rounded-full px-2 py-0.5 text-xs font-normal"
                >
                  {label.key}
                  <ChevronDown className="size-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-w-80">
                {label.children.map((child) => {
                  const text = `${child.key}: ${child.value}`;
                  return (
                    <DropdownMenuItem
                      key={`${label.key}-${child.key}`}
                      onSelect={(event) => event.preventDefault()}
                      className="max-w-80 cursor-default focus:bg-transparent"
                    >
                      <TruncatedHoverText text={text} className="max-w-72" />
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge
              key={label.key}
              variant="secondary"
              className="text-xs font-normal"
            >
              {label.key}
            </Badge>
          )
        ) : label.references ? (
          <DropdownMenu key={label.key}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-5 rounded-full px-2 py-0.5 text-xs font-normal"
              >
                {label.key}: {label.value}
                <ChevronDown className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-w-80">
              {label.references.length === 0 ? (
                <DropdownMenuItem disabled>
                  No references selected
                </DropdownMenuItem>
              ) : (
                label.references.map((reference) =>
                  reference.href ? (
                    <DropdownMenuItem key={reference.id} asChild>
                      <a
                        href={reference.href}
                        target="_blank"
                        rel="noreferrer"
                        className="flex min-w-0 items-center gap-2"
                      >
                        <span className="truncate">{reference.name}</span>
                        <ExternalLink className="ml-auto size-3 shrink-0" />
                      </a>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem key={reference.id} disabled>
                      <span className="truncate">{reference.name}</span>
                    </DropdownMenuItem>
                  ),
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Badge
            key={label.key}
            variant="secondary"
            className="max-w-80 text-xs font-normal"
          >
            <TruncatedHoverText
              text={`${label.key}: ${label.value}`}
              className="max-w-72"
            />
          </Badge>
        ),
      )}
    </div>
  );
}
